import {
  ACTION_STACK_LIMIT,
  appendActionEntry,
  appendActionEntryInPlace,
  createInitialActionStackState,
} from '../actionStack';
import { cloneRepathingCurve } from './repathingCurve';
import type {
  RepathingActionEntry,
  RepathingActionKind,
  RepathingActionSnapshot,
  RepathingActionStackState,
  RepathingControlSelection,
  RepathingCurve,
} from './types';

export const REPATHING_ACTION_STACK_LIMIT = ACTION_STACK_LIMIT;

export function createInitialRepathingActionStackState(): RepathingActionStackState {
  return createInitialActionStackState<RepathingActionEntry>();
}

export function cloneRepathingActionSnapshot(
  snapshot: RepathingActionSnapshot,
): RepathingActionSnapshot {
  return {
    curve: snapshot.curve ? cloneRepathingCurve(snapshot.curve) : null,
    selectedControl: cloneRepathingControlSelection(snapshot.selectedControl),
  };
}

export function createRepathingActionEntry(input: {
  kind: RepathingActionKind;
  label: string;
  before: RepathingActionSnapshot;
  after: RepathingActionSnapshot;
}): RepathingActionEntry {
  return {
    id: createRepathingActionId(),
    kind: input.kind,
    label: input.label,
    before: cloneRepathingActionSnapshot(input.before),
    after: cloneRepathingActionSnapshot(input.after),
    timestamp: Date.now(),
  };
}

export function appendRepathingActionEntry(
  actionStack: RepathingActionStackState,
  entry: RepathingActionEntry,
  maxLength = REPATHING_ACTION_STACK_LIMIT,
): RepathingActionStackState {
  return appendActionEntry(actionStack, entry, maxLength);
}

export function appendRepathingActionEntryInPlace(
  actionStack: RepathingActionStackState,
  entry: RepathingActionEntry,
  maxLength = REPATHING_ACTION_STACK_LIMIT,
): RepathingActionStackState {
  return appendActionEntryInPlace(actionStack, entry, maxLength);
}

export function areRepathingActionSnapshotsEqual(
  left: RepathingActionSnapshot,
  right: RepathingActionSnapshot,
) {
  return (
    getRepathingActionSnapshotSignature(left) ===
    getRepathingActionSnapshotSignature(right)
  );
}

function cloneRepathingControlSelection(
  selection: RepathingControlSelection | null,
): RepathingControlSelection | null {
  return selection ? { ...selection } : null;
}

function getRepathingActionSnapshotSignature(
  snapshot: RepathingActionSnapshot,
) {
  return JSON.stringify(buildComparableRepathingCurve(snapshot.curve));
}

function buildComparableRepathingCurve(curve: RepathingCurve | null) {
  if (!curve) {
    return null;
  }

  return {
    frameCount: curve.frameCount,
    sourceFrameCount: curve.sourceFrameCount,
    durationSec: curve.durationSec,
    points: curve.points.map((point) => ({
      id: point.id,
      u: point.u,
      x: point.x,
      z: point.z,
      inHandle: point.inHandle
        ? { x: point.inHandle.x, z: point.inHandle.z }
        : null,
      outHandle: point.outHandle
        ? { x: point.outHandle.x, z: point.outHandle.z }
        : null,
    })),
    sourceHeadings: curve.sourceHeadings.map((heading) => ({
      u: heading.u,
      heading: heading.heading,
    })),
  };
}

function createRepathingActionId() {
  return `repathing-action-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}
